/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/** POST /re/units/{id}/holds — put a unit on hold for a buyer (EOI). */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, TextField,
} from '@mui/material';
import MDButton from 'components/MDButton';
import { ContactPicker, LeadPicker, RePicker } from './Pickers';
import { errorMessage, toNumber, useReApi } from '../api';
import { defaultCurrency } from '../../variant';

export default function HoldDialog({ open, unit, onClose, onDone }) {
  const { api } = useReApi();
  const [form, setForm] = useState({ hours: 48, contact_id: null, lead_id: null, broker_firm_id: null, eoi_amount: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ hours: 48, contact_id: null, lead_id: null, broker_firm_id: null, eoi_amount: '', notes: '' });
      setErr(null);
    }
  }, [open]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const hours = parseInt(form.hours, 10);
    if (!hours || hours < 1 || hours > 720) {
      setErr('Hold hours must be between 1 and 720.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const body = {
        hours,
        contact_id: form.contact_id || undefined,
        lead_id: form.lead_id || undefined,
        broker_firm_id: form.broker_firm_id || undefined,
        eoi_amount: toNumber(form.eoi_amount) ?? undefined,
        notes: form.notes || undefined,
      };
      const res = await api.post(`/re/units/${unit.id}/holds`, body);
      onDone(res.data);
    } catch (e) {
      setErr(errorMessage(e, 'Could not place the hold'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{`Hold unit ${unit?.unit_number || ''}`}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" type="number" label="Hold for" value={form.hours}
              onChange={(e) => set('hours')(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">hours</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" type="number" label={`EOI amount (${defaultCurrency()})`} value={form.eoi_amount}
              onChange={(e) => set('eoi_amount')(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <ContactPicker label="Buyer contact" value={form.contact_id} onChange={set('contact_id')} sx={{ width: '100%' }} allowCreate />
          </Grid>
          <Grid item xs={12} sm={6}>
            <LeadPicker label="Lead (optional)" value={form.lead_id} onChange={set('lead_id')} sx={{ width: '100%' }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RePicker path="/re/broker-firms" label="Broker firm (optional)" value={form.broker_firm_id} onChange={set('broker_firm_id')} sx={{ width: '100%' }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={2} label="Notes" value={form.notes} onChange={(e) => set('notes')(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="warning" onClick={submit} disabled={saving}>
          {saving ? 'Holding…' : 'Place hold'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

HoldDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  unit: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};
HoldDialog.defaultProps = { unit: null };
